import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, inject, input, model } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";

import { PassaggioConsegneRequest, PassaggioConsegneResponse } from "../models/passaggio-consegne.model";
import { PassaggioConsegneService } from "../services/passaggio-consegne.service";
import { PassaggioConsegneModuliBuilderService } from "../services/passaggio-consegne-moduli-builder.service";

@Component({
  selector: "app-passaggio-download-modulo-dialog",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog [(visible)]="isOpen" [modal]="true" [closable]="false" [closeOnEscape]="false" [resizable]="true" [style]="{ height: '350px', width: '600px' }">
      <p-header>Modulo passaggio consegne</p-header>
      <form [formGroup]="headerForm" (ngSubmit)="printAndUpdate()">
      <div class="grid">
        <div class="col-12">
          <p-dropdown [options]="classificaOptions" optionLabel="label" optionValue="value" [showClear]="true" placeholder="Seleziona la classifica del documento" formControlName="classifica" appendTo="body"/>
        </div>
        <div class="col-12">
          <p-selectButton [options]="verificaOptions" formControlName="tipoVerifica" optionLabel="label"  optionValue="value" />
        </div>
        <div class="col-12">
          <textarea rows="4" cols="80" placeholder="Inserisci la lista dei progressivi separata da virgola (,)" formControlName="progressiviCampione" pInputTextarea></textarea>
        </div>
      </div>
      </form>
      <p-footer>
        <button pButton type="button" label="Stampa" title="Stampa" (click)="printAndUpdate()" class="mr-2" [disabled]="!headerForm.valid"></button>
        <button pButton type="button" label="Chiudi" title="Chiudi" (click)="close()" class="p-button-secondary"></button>
      </p-footer>
    </p-dialog>
  `,
})
export class PassaggioDownloadModuloDialog implements OnChanges {
  private readonly passaggioConsegneService = inject(PassaggioConsegneService);
  private readonly passaggioConsegneModuliBuilder = inject(PassaggioConsegneModuliBuilderService);
  private readonly fb = inject(FormBuilder);

  public isOpen = model<boolean>(true);
  public passaggioConsegne = input<PassaggioConsegneResponse>();

  public readonly classificaOptions = [
    { label: "Riservato", value: "R" },
    { label: "Riservatissimo, Segreto, Segretissimo", value: "RR" },
  ];

  public readonly verificaOptions = [
    { label: 'Verifica completa', value: 'completa' },
    { label: 'Verifica a campione', value: 'campione' }
  ]

  public headerForm = this.fb.group<{classifica: FormControl<string | null>, tipoVerifica: FormControl<string | null>, progressiviCampione: FormControl<string | null>}>({
    classifica: new FormControl(null, [Validators.required]),
    tipoVerifica: new FormControl({ value: null, disabled: true }),
    progressiviCampione: new FormControl({ value: '', disabled: true }),
  }, { validators: this.validateForm });

  constructor() {
    this.headerForm.get('classifica')?.valueChanges
      .subscribe((data) => {
        this.headerForm.get('tipoVerifica')?.disable();
        this.headerForm.patchValue({ 'tipoVerifica': null });
        if (data == "R") {
          this.headerForm.get('tipoVerifica')?.enable();
        }
      });

    this.headerForm.get('tipoVerifica')?.valueChanges
      .subscribe((data) => {
        this.headerForm.get('progressiviCampione')?.disable();
        if (data == "campione") {
          this.headerForm.get('progressiviCampione')?.enable();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.headerForm.patchValue({ 'progressiviCampione': this.passaggioConsegne()?.progressiviCampione });
  }

  public close(): void {
    this.headerForm.reset();
    this.isOpen.set(false);
  }

  public printAndUpdate() {
    const tipoVerifica = this.headerForm.value.tipoVerifica;
    const progressivi = this.headerForm.value.progressiviCampione;

    this.passaggioConsegneModuliBuilder.setData(tipoVerifica, progressivi, this.passaggioConsegne());
    this.passaggioConsegneModuliBuilder.generatePDF();

    this.updatePassaggio();
    this.close();
  }

  private updatePassaggio() {
    const request: PassaggioConsegneRequest = {
      funzionarioUscente: this.passaggioConsegne()?.funzionarioUscente ?? null,
      funzionarioSubentrante: this.passaggioConsegne()?.funzionarioSubentrante ?? null,
      dataPassaggioConsegne: this.passaggioConsegne()?.dataPassaggioConsegne ?? null,
      progressiviCampione: this.headerForm.value.progressiviCampione
    }
    this.passaggioConsegneService.update(request, this.passaggioConsegne()?.passaggioConsegneId ?? -1).subscribe();
  }

  private validateForm(control: AbstractControl): ValidationErrors | null {
    const headerForm = control as FormGroup;
    const classifica = headerForm.get('classifica')?.value;
    const tipoVerifica = headerForm.get('tipoVerifica')?.value;
    const progressiviCampione = `${headerForm.get('progressiviCampione')?.value ?? ''}`;

    const numProgressiviCampione = progressiviCampione.split(",").map( (p:string)=> p.trim()).filter( (p: string)=> p !== '').length;

    let isValid = false;

    if (classifica && classifica != 'R' ||
      classifica == 'R' && tipoVerifica == 'completa' ||
      classifica == 'R' && tipoVerifica == 'campione' && numProgressiviCampione >= 10) {
      isValid = true;
    }

    return isValid ? null : { formInvalid: true };
  }
}